import { Listing } from '../api';

/** Matches buyer apps: always two decimal places (e.g. $12.00). */
export function formatListingPrice(price: number | null | undefined): string {
  if (price == null || price === 0) return 'Free';
  const num = Number(price);
  if (!Number.isFinite(num)) return 'Free';
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function isRunSale(listing: {
  discount_type?: string | null;
  discount_value?: number | null;
  discount_min_quantity?: number | null;
}): boolean {
  const dv = listing.discount_value;
  if (dv == null || dv <= 0 || !listing.discount_type) return false;
  return (listing.discount_min_quantity ?? 2) <= 1;
}

export function salePrice(listing: {
  price: number | null;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_min_quantity?: number | null;
}): number | null {
  if (!isRunSale(listing)) return null;
  const listPrice = Number(listing.price) || 0;
  const dv = Number(listing.discount_value) || 0;
  if (listPrice <= 0 || dv <= 0) return null;
  if (listing.discount_type === 'percentage') {
    return roundMoney(listPrice * (1 - Math.min(dv, 100) / 100));
  }
  return roundMoney(Math.max(0, listPrice - dv));
}

function roundMoney(amount: number): number {
  return Math.round(amount * 100) / 100;
}

type DiscountableListing = {
  price: number | null;
  discount_type?: string | null;
  discount_value?: number | null;
  discount_min_quantity?: number | null;
};

/** Line total for checkout/cart — run sale + bulk quantity discounts (matches CartContext). */
export function checkoutLineTotal(listing: DiscountableListing, quantity: number): number {
  const basePrice = Number(listing.price) || 0;
  const qty = Math.max(1, quantity);

  if (listing.discount_type && listing.discount_value != null && Number(listing.discount_value) > 0) {
    const minQuantity = listing.discount_min_quantity ?? 2;
    if (qty >= minQuantity) {
      const dv = Number(listing.discount_value);
      if (listing.discount_type === 'percentage') {
        return roundMoney(basePrice * qty * (1 - Math.min(dv, 100) / 100));
      }
      if (listing.discount_type === 'fixed') {
        return roundMoney(Math.max(0, basePrice * qty - dv * qty));
      }
    }
  }

  return roundMoney(cardDisplayPrice(listing) * qty);
}

export function calculateCheckoutSubtotal(
  listings: Array<{ id: number } & DiscountableListing>,
  quantities: Record<number, number>
): number {
  const sum = listings.reduce((acc, l) => {
    const qty = quantities[l.id] || 1;
    return acc + checkoutLineTotal(l, qty);
  }, 0);
  return roundMoney(sum);
}

export function cardDisplayPrice(listing: { price: number | null } & Parameters<typeof isRunSale>[0]): number {
  const sale = salePrice(listing);
  if (sale != null) return sale;
  return Number(listing.price) || 0;
}

export function strikethroughListPrice(
  listing: { price: number | null } & Parameters<typeof isRunSale>[0]
): number | null {
  if (!isRunSale(listing)) return null;
  const listPrice = Number(listing.price) || 0;
  return listPrice > 0 ? listPrice : null;
}

/** Sale: "Sale 25% off". Bulk: "Buy 3+ and save …". */
export function discountBadgeText(listing: Listing | null): string | null {
  if (!listing?.discount_type || listing.discount_value == null || listing.discount_value <= 0) {
    return null;
  }
  const minQty = listing.discount_min_quantity ?? 2;
  const val = Number(listing.discount_value);
  if (minQty <= 1) {
    if (listing.discount_type === 'percentage') {
      return `Sale ${Math.round(val)}% off`;
    }
    if (listing.discount_type === 'fixed') {
      return `Sale $${val.toFixed(2)} off`;
    }
    return null;
  }
  if (listing.discount_type === 'percentage') {
    return `Buy ${minQty}+ and save ${val}%`;
  }
  if (listing.discount_type === 'fixed') {
    return `Buy ${minQty}+ and save $${val.toFixed(2)}`;
  }
  return null;
}
